import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { APP_ID, ApplicationConfig, ErrorHandler, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { GlobalErrorHandler } from '@interceptors/global-error-handler.interceptor';
import { HttpErrorInterceptor, HttpTokenInterceptor } from '@interceptors/http-interceptor';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { appRoutes } from '@routes/app.routes';
import { Observable, of } from 'rxjs';
import { de_CH, en_US, es_ES, fi_FI, fr_CH, it_CH, nl_NL, pl_PL, sv_SE } from './i18n/';
import { environment } from '@env/environment';

// locals import required for currency pipe and date pipe
import localeDe from '@angular/common/locales/de';
import localeDeCH from '@angular/common/locales/de-CH';
import localeEs from '@angular/common/locales/es';
import localFi from '@angular/common/locales/fi';
import localeFr from '@angular/common/locales/fr';
import localeFrCH from '@angular/common/locales/fr-CH';
import localeIt from '@angular/common/locales/it';
import localeItCh from '@angular/common/locales/it-CH';
import localeNl from '@angular/common/locales/nl';
import localePl from '@angular/common/locales/pl';
import localeSv from '@angular/common/locales/sv';
import { TraceService } from '@sentry/angular';

// register locals for currency pipe and date pipe
registerLocaleData(localeDe, 'de');
registerLocaleData(localeDeCH, 'ch');
registerLocaleData(localeEs, 'es');
registerLocaleData(localFi, 'fi');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeFrCH, 'fr_CH');
registerLocaleData(localeIt, 'it');
registerLocaleData(localeItCh, 'it_CH');
registerLocaleData(localeNl, 'nl');
registerLocaleData(localePl, 'pl');
registerLocaleData(localeSv, 'sv');

const TRANSLATION_MAP: Record<string, any> = {
  de_CH, en_US, fi_FI, fr_CH, it_CH, nl_NL, pl_PL, es_ES, sv_SE,
};

export class CustomTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<any> {
    return of(TRANSLATION_MAP[lang] || TRANSLATION_MAP[environment.defaultLocale]);
  }
}

export function getLocalStorage() {
  return (typeof window !== 'undefined') ? window.localStorage : null;
}

export function getSessionStorage() {
  return (typeof window !== 'undefined') ? window.sessionStorage : null;
}

const interceptors = [HttpTokenInterceptor, HttpErrorInterceptor];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideClientHydration(withEventReplay(), withHttpTransferCacheOptions({ includePostRequests: true })),
    provideAnimations(),
    provideHttpClient(
      withFetch(),
      withInterceptors(interceptors)
    ),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: () => new CustomTranslateLoader(),
      },
    }),
    importProvidersFrom(MatDialogModule),
    { provide: 'LOCALSTORAGE', useFactory: getLocalStorage },
    { provide: 'SESSIONSTORAGE', useFactory: getSessionStorage },
    { provide: LOCALE_ID, useValue: environment.defaultLocale.replace('_', '-').toLowerCase() },
    { provide: APP_ID, useValue: 'calio-booking-page' },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideAppInitializer(() => {
      inject(TraceService);
    }),
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } as RippleGlobalOptions },
    CalioTranslationPipe
  ]
};
